import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn
} from '@angular/common/http';
import { inject } from '@angular/core';

import {
  AUTH_ENDPOINTS,
  AUTH_POST_FORGOT_PASSWORD_ENDPOINT,
  AUTH_POST_RESET_PASSWORD_ENDPOINT,
  AUTH_POST_VERIFY_EMAIL_ENDPOINT,
  AUTH_POST_VERIFICATION_EMAIL_ENDPOINT,
} from '../api/api-endpoints';
import { extractApiErrorCode } from '../api/api-error';
import { TokenService } from '../services/token.service';
import { AuthService, isTransientAuthError } from '../services/auth.service';
import { BackendConnectivityService } from '../services/backend-connectivity.service';
import {
  catchError,
  switchMap,
  throwError,
  BehaviorSubject,
  filter,
  take,
  from,
  finalize,
  tap,
} from 'rxjs';

let isRefreshing = false;
let refreshSubject = new BehaviorSubject<string | null>(null);

const AUTH_BYPASS_PATHS = [
  AUTH_ENDPOINTS.login,
  AUTH_ENDPOINTS.register,
  AUTH_ENDPOINTS.register_invited,
  AUTH_ENDPOINTS.refresh,
  AUTH_POST_FORGOT_PASSWORD_ENDPOINT,
  AUTH_POST_RESET_PASSWORD_ENDPOINT,
  AUTH_POST_VERIFY_EMAIL_ENDPOINT,
  AUTH_POST_VERIFICATION_EMAIL_ENDPOINT,
];

const OAUTH_PATH_REGEX = /\/auth\/oauth2\/[^/]+\/(start|callback)/i;
export const SKIP_AUTH = new HttpContextToken<boolean>(() => false);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const authService = inject(AuthService);
  const backendConnectivity = inject(BackendConnectivityService);

  const bypassAuth = req.context.get(SKIP_AUTH) || shouldBypassAuth(req.url);
  const token = tokenService.getAccessToken();

  let authReq = req;

  if (token && !bypassAuth) {
    authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      backendConnectivity.recordError(req.url, error);

      if (bypassAuth) {
        return throwError(() => error);
      }

      if (!shouldAttemptTokenRefresh(error)) {
        return throwError(() => error);
      }

      const refreshToken = tokenService.getRefreshTokenForAccess(token);
      if (!refreshToken) {
        authService.logout(token);
        return throwError(() => error);
      }

      if (isRefreshing) {
        return refreshSubject.pipe(
          filter((token): token is string => token !== null),
          take(1),
          switchMap(newToken => {
            const retryReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${newToken}`
              }
            });
            return next(retryReq);
          }),
          catchError(err => throwError(() => err))
        );
      }

      isRefreshing = true;
      refreshSubject.next(null);

      return from(authService.refreshToken(token)).pipe(
        switchMap(() => {
          const newToken = tokenService.getAccessToken();

          if (!newToken) {
            return throwError(() => new Error('Failed to refresh access token.'));
          }

          refreshSubject.next(newToken);
          const retryReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken}`
            }
          });

          return next(retryReq);
        }),
        catchError(err => {
          refreshSubject.error(err);
          refreshSubject = new BehaviorSubject<string | null>(null);

          if (!isTransientAuthError(err)) {
            authService.logout(token);
          }

          return throwError(() => err);
        }),
        finalize(() => {
          isRefreshing = false;
        })
      );
    }),
    tap(() => backendConnectivity.recordSuccess(req.url)),
  );
};

function shouldBypassAuth(url: string): boolean {
  const normalized = (url || '').toLowerCase();

  if (OAUTH_PATH_REGEX.test(normalized)) {
    return true;
  }

  return AUTH_BYPASS_PATHS.some((path) => normalized.includes(path.toLowerCase()));
}

function shouldAttemptTokenRefresh(error: HttpErrorResponse): boolean {
  const code = extractApiErrorCode(error);

  if (code === 'AUTH_TOKEN_EXPIRED' || code === 'AUTH_INVALID_TOKEN') {
    return true;
  }

  if (error?.status !== 401) {
    return false;
  }

  const message = extractErrorMessage(error).toLowerCase();
  if (!message) {
    return false;
  }

  return message.includes('expired token') || message.includes('token expired');
}

function extractErrorMessage(error: HttpErrorResponse): string {
  const values: unknown[] = [
    (error?.error as any)?.message,
    (error?.error as any)?.error?.message,
    (error?.error as any)?.description,
    (error?.error as any)?.error?.description,
    (error as any)?.message,
  ];

  for (const value of values) {
    const message = asMessage(value);
    if (message) {
      return message;
    }
  }

  return '';
}

function asMessage(value: unknown): string | null {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    try {
      const parsed = JSON.parse(trimmed);
      return asMessage((parsed as any)?.message) || trimmed;
    } catch {
      return trimmed;
    }
  }

  if (typeof value === 'object') {
    const message = (value as Record<string, unknown>)['message'];
    if (typeof message === 'string' && message.trim().length > 0) {
      return message.trim();
    }
  }

  return null;
}
